<template>
	<div class="properties-by-broker-widget">
		<div class="properties-by-broker-widget__header">
			<h3 class="properties-by-broker-widget__title">Properties by Broker</h3>
			
			<themed-tooltip 
				placement="bottom"
				:show-delay="300"
				max-width="300px"
			>
				<button 
					class="properties-by-broker-widget__info" 
					aria-label="More information about Properties by Broker"
					@mouseenter="loadTooltip('dashboard-properties-by-broker')"
				>
					<svg 
						width="14" 
						height="14" 
						viewBox="0 0 24 24" 
						fill="none" 
						stroke="currentColor" 
						stroke-width="2" 
						stroke-linecap="round" 
						stroke-linejoin="round"
					>
						<circle cx="12" cy="12" r="10"></circle>
						<line x1="12" y1="16" x2="12" y2="12"></line>
						<line x1="12" y1="8" x2="12.01" y2="8"></line>
					</svg>
				</button>
				<template #content>
					<div v-html="getTooltipContent('dashboard-properties-by-broker')"></div>
				</template>
			</themed-tooltip>
		</div>
		
		<div class="properties-by-broker-widget__content">
			<!-- Loading State -->
			<div v-if="isLoading" class="properties-by-broker-widget__loading">
				<svg class="properties-by-broker-widget__spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
					<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
					<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
				</svg>
				<span>Loading...</span>
			</div>

			<!-- Error State -->
			<div v-else-if="hasError" class="properties-by-broker-widget__error">
				<p>Failed to load broker data</p>
				<button @click="fetchBrokerData" class="properties-by-broker-widget__retry">Retry</button>
			</div>

			<!-- Data Display -->
			<div v-else>
				<!-- Donut Chart -->
				<div class="properties-by-broker-widget__chart">
					<!-- <fusioncharts
						type="doughnut2d"
						width="100%"
						height="220"
						:dataSource="chartDataSource"
					></fusioncharts> -->


					<theme-doughnut-chart
					:data="doughnutChartData"
					:colors="chartColors"
					:show-percentages="false"
					:show-legend="false"
					cutout="70%"
				/>
					<div class="properties-by-broker-widget__chart-center">
						<div class="properties-by-broker-widget__total-count">{{ totalProperties }}</div>
						<div class="properties-by-broker-widget__total-label">TOTAL</div>
					</div>
				</div>

				<!-- Broker Statistics List -->
				<div class="properties-by-broker-widget__list">
					<div
						v-for="broker in displayBrokers"
						:key="broker.broker_id || 'other'"
						class="properties-by-broker-widget__list-item"
					>
						<div class="properties-by-broker-widget__list-left">
							<span 
								class="properties-by-broker-widget__dot"
								:style="{ backgroundColor: broker.colorHex }"
							></span>
							<span class="properties-by-broker-widget__broker-name">{{ broker.broker_name }}</span>
						</div>
						<div class="properties-by-broker-widget__list-right">
							<span class="properties-by-broker-widget__count">Count: {{ broker.property_count }}</span>
							<span class="properties-by-broker-widget__percentage">{{ broker.percentage }}%</span>
						</div>
					</div>

					<!-- Brokers can share a client, so the counts above overlap and
					     deliberately add up to more than the distinct total. -->
					<p v-if="hasOverlap" class="properties-by-broker-widget__footnote">
						Brokers can cover the same client, so these counts overlap.
					</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import ThemedTooltip from '../ThemedTooltip.vue';
import axios from 'axios';
import ThemeDoughnutChart from '../charts/ThemeDoughnutChart.vue';

export default {
	name: 'PropertiesByBrokerWidget',
	components: {
		ThemedTooltip,
		ThemeDoughnutChart
	},
	data() {
		return {
			brokerData: [],
			totalDistinctProperties: 0,
			isLoading: true,
			hasError: false,
			tooltipCache: {}
		}
	},
	mounted() {
		this.fetchBrokerData();
	},
	computed: {
		totalProperties() {
			// Distinct count from the API. Brokers share clients, so summing the
			// slices counts the same property once per broker who can see it.
			return this.totalDistinctProperties;
		},

		hasOverlap() {
			return this.chartTotal > this.totalProperties;
		},

		chartTotal() {
			// What the arcs actually add up to — the basis for the listed
			// percentages, so the numbers agree with the doughnut.
			return _.sumBy(this.brokerData, 'property_count') || 0;
		},
		
		topBrokers() {
			// Get top 4 brokers by property count
			return _.take(_.orderBy(this.brokerData, ['property_count'], ['desc']), 4);
		},
		
		otherBrokers() {
			// Get remaining brokers beyond top 4
			return _.drop(_.orderBy(this.brokerData, ['property_count'], ['desc']), 4);
		},
		
		chartColors() {
			return ['#60a5fa', '#34d399', '#fb923c', '#a78bfa', '#9ca3af'];
		},
		
		displayBrokers() {
			const colors = this.chartColors;
			
			// Map top brokers with colors and percentages
			const topBrokersWithData = this.topBrokers.map((broker, index) => ({
				...broker,
				colorHex: colors[index],
				percentage: this.calculatePercentage(broker.property_count)
			}));
			
			// If there are more than 4 brokers, create "Other" category
			if (this.otherBrokers.length > 0) {
				const otherCount = _.sumBy(this.otherBrokers, 'property_count');
				const otherBroker = {
					broker_id: null,
					broker_name: 'Other',
					property_count: otherCount,
					colorHex: colors[4],
					percentage: this.calculatePercentage(otherCount)
				};
				return [...topBrokersWithData, otherBroker];
			}
			
			return topBrokersWithData;
		},
		
		doughnutChartData() {
			// Data formatted for ThemeDoughnutChart component
			return this.displayBrokers.map(broker => ({
				label: broker.broker_name,
				value: broker.property_count
			}));
		},
		
		chartDataSource() {
			const colors = this.displayBrokers.map(b => b.colorHex.replace('#', ''));
			
			return {
				chart: {
					bgColor: '#FFFFFF',
					bgAlpha: '0',
					canvasBgColor: '#FFFFFF',
					canvasBgAlpha: '0',
					baseFont: '"-apple-system", "BlinkMacSystemFont", "Segoe UI", "Roboto", "Helvetica Neue", "Arial", "Noto Sans", "sans-serif"',
					baseFontSize: 14,
					caption: '',
					showLabels: '0',
					showValues: '0',
					showPercentValues: '0',
					showLegend: '0',
					enableSmartLabels: '0',
					startingAngle: '90',
					use3DLighting: '0',
					theme: 'fusion',
					defaultCenterLabel: '',
					centerLabel: '',
					doughnutRadius: '70',
					pieRadius: '85',
					paletteColors: colors.join(',')
				},
				data: this.displayBrokers.map((broker, index) => ({
					label: broker.broker_name,
					value: broker.property_count,
					color: colors[index]
				}))
			}
		}
	},
	methods: {
		async fetchBrokerData() {
			this.isLoading = true;
			this.hasError = false;
			
			try {
				const response = await axios.get('/api/dashboard/broker-properties');
				this.brokerData = response.data.brokers || [];
				this.totalDistinctProperties = response.data.total_properties || 0;
			} catch (error) {
				console.error('Error fetching broker data:', error);
				this.hasError = true;
			} finally {
				this.isLoading = false;
			}
		},
		
		calculatePercentage(count) {
			if (this.chartTotal === 0) {
				return 0;
			}
			return Math.round((count / this.chartTotal) * 100);
		},
		
		getTooltipContent(identifier) {
			if (this.tooltipCache[identifier]) {
				return this.tooltipCache[identifier];
			}
			return '<div style="display: flex; align-items: center; gap: 0.5rem;"><svg class="animate-spin" style="width: 1rem; height: 1rem;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg><span>Loading...</span></div>';
		},
		
		async loadTooltip(identifier) {
			if (!this.tooltipCache[identifier]) {
				this.$set(this.tooltipCache, identifier, '<div style="display: flex; align-items: center; gap: 0.5rem;"><svg class="animate-spin" style="width: 1rem; height: 1rem;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg><span>Loading...</span></div>');
				
				try {
					const response = await axios.get(`/api/tooltips/identifier/${identifier}`);
					this.$set(this.tooltipCache, identifier, response.data.content || 'Tooltip content not available');
				} catch (err) {
					console.error('Error loading tooltip:', err);
					if (err.response && err.response.status === 404) {
						this.$set(this.tooltipCache, identifier, 'Tooltip content is not yet available for this field.');
					} else {
						this.$set(this.tooltipCache, identifier, 'Failed to load information');
					}
				}
			}
		}
	}
}
</script>
